// services/NotificationDispatcher.js - VERSION CORRIGÉE
import {
  collection,
  addDoc,
  getDoc,
  doc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import emailjs from '@emailjs/browser';

class NotificationDispatcher {
  constructor() {
    // Configuration EmailJS
    this.emailjsServiceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    this.emailjsTemplateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    this.emailjsPublicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    // Configuration SMS (exemple)
    this.twilioAccountSid = process.env.REACT_APP_TWILIO_ACCOUNT_SID;
    this.twilioAuthToken = process.env.REACT_APP_TWILIO_AUTH_TOKEN;
    this.twilioPhoneNumber = process.env.REACT_APP_TWILIO_PHONE_NUMBER;

    if (this.emailjsPublicKey) {
      emailjs.init(this.emailjsPublicKey);
    }
  }

  // ✅ NOUVEAU : Fonction pour nettoyer les données undefined
  cleanNotificationData(data) {
    const cleaned = {};
    
    // Copier seulement les champs qui ne sont pas undefined
    Object.keys(data).forEach(key => {
      if (data[key] !== undefined && data[key] !== null) {
        cleaned[key] = data[key];
      }
    });
    
    return cleaned;
  }

  // Envoi principal
  async sendNotification(notification) {
    try {
      const { recipientId, urgency = 'normal' } = notification;

      const userPrefs = await this.getUserNotificationPreferences(recipientId);

      const notificationDoc = await this.saveNotificationToFirestore(notification);

      const promises = [];

      if (userPrefs.pushNotifications || urgency === 'urgent') {
        promises.push(this.sendPushNotification(recipientId, notification));
      }
      if (userPrefs.emailNotifications || urgency === 'urgent') {
        promises.push(this.sendEmailNotification(recipientId, notification));
      }
      if ((userPrefs.smsNotifications && urgency === 'urgent') ||
          (userPrefs.smsNotifications && userPrefs.smsForAllNotifications)) {
        promises.push(this.sendSMSNotification(recipientId, notification));
      }

      const results = await Promise.allSettled(promises);

      await this.updateNotificationStatus(notificationDoc.id, results);

      return {
        success: true,
        notificationId: notificationDoc.id,
        deliveryResults: results
      };
    } catch (error) {
      console.error('Erreur lors de l\'envoi de la notification:', error);
      throw error;
    }
  }

  // Préférences utilisateur
  async getUserNotificationPreferences(userId) {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const data = userDoc.data();
        return {
          emailNotifications: data.emailNotifications !== false,
          smsNotifications: data.smsNotifications === true,
          pushNotifications: data.pushNotifications !== false,
          smsForAllNotifications: data.smsForAllNotifications === true,
          reminderDelay: data.reminderDelay || 7
        };
      }
      return {
        emailNotifications: true,
        smsNotifications: false,
        pushNotifications: true,
        smsForAllNotifications: false,
        reminderDelay: 7
      };
    } catch (error) {
      console.error('Erreur récupération préférences:', error);
      return {
        emailNotifications: true,
        smsNotifications: false,
        pushNotifications: true,
        smsForAllNotifications: false,
        reminderDelay: 7
      };
    }
  }

  // ✅ CORRIGÉ : Sauvegarde Firestore avec nettoyage des undefined
  async saveNotificationToFirestore(notification) {
    try {
      // Créer les données de base
      const baseNotificationData = {
        title: notification.title || 'Notification',
        message: notification.message || '',
        type: notification.type || 'info',
        createdAt: serverTimestamp(),
        read: false,
        lu: false, // Compatibilité
        deleted: false,
        deliveryStatus: {
          push: 'pending',
          email: 'pending',
          sms: 'pending'
        }
      };

      // Ajouter les champs optionnels seulement s'ils existent
      if (notification.recipientId) {
        baseNotificationData.userId = notification.recipientId;
      }
      
      if (notification.senderId) {
        baseNotificationData.senderId = notification.senderId;
        baseNotificationData.participants = [notification.senderId, notification.recipientId];
        baseNotificationData.readBy = [notification.senderId];
      }
      
      if (notification.dossierId) {
        baseNotificationData.dossierId = notification.dossierId;
      }
      
      if (notification.dossierRef) {
        baseNotificationData.dossierRef = notification.dossierRef;
      }
      
      if (notification.clientId) {
        baseNotificationData.clientId = notification.clientId;
      }
      
      if (notification.urgency) {
        baseNotificationData.urgency = notification.urgency;
      }

      // ✅ NOUVEAU : Nettoyer les données avant l'envoi
      const cleanedData = this.cleanNotificationData(baseNotificationData);
      
      console.log('📧 DISPATCHER: Données nettoyées pour Firestore:', cleanedData);
      
      const docRef = await addDoc(collection(db, 'notifications'), cleanedData);
      return docRef;
    } catch (error) {
      console.error("❌ DISPATCHER: Erreur saveNotificationToFirestore:", error);
      throw error;
    }
  }

  // Push
  async sendPushNotification(recipientId, notification) {
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        throw new Error('Push notifications not supported');
      }

      const userDoc = await getDoc(doc(db, 'users', recipientId));
      const fcmToken = userDoc.data()?.fcmToken;

      if (!fcmToken) {
        throw new Error('No FCM token found for user');
      }

      const response = await fetch('/api/send-push-notification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: fcmToken,
          title: notification.title,
          body: notification.message,
          data: {
            dossierRef: notification.dossierRef,
            clientId: notification.clientId,
            type: notification.type
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send push notification');
      }

      return { success: true, method: 'push' };
    } catch (error) {
      console.error('Erreur notification push:', error);
      return { success: false, method: 'push', error: error.message };
    }
  }

  // Email
  async sendEmailNotification(recipientId, notification) {
    try {
      const userDoc = await getDoc(doc(db, 'users', recipientId));
      const userEmail = userDoc.data()?.email;

      if (!userEmail) {
        throw new Error('No email found for user');
      }

      const emailTemplate = this.getEmailTemplate(notification);

      const result = await emailjs.send(
        this.emailjsServiceId,
        this.emailjsTemplateId,
        {
          to_email: userEmail,
          to_name: userDoc.data()?.displayName || 'Utilisateur',
          subject: notification.title,
          message: notification.message,
          html_content: emailTemplate,
          dossier_ref: notification.dossierRef || '',
          urgency: notification.urgency || 'normal'
        }
      );

      return { success: true, method: 'email', result };
    } catch (error) {
      console.error('Erreur envoi email:', error);
      return { success: false, method: 'email', error: error.message };
    }
  }

  // SMS
  async sendSMSNotification(recipientId, notification) {
    try {
      const userDoc = await getDoc(doc(db, 'users', recipientId));
      const phoneNumber = userDoc.data()?.phoneNumber;

      if (!phoneNumber) {
        throw new Error('No phone number found for user');
      }

      const response = await fetch('/api/send-sms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: phoneNumber,
          message: `${notification.title}: ${notification.message}`,
          urgency: notification.urgency
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send SMS');
      }

      return { success: true, method: 'sms' };
    } catch (error) {
      console.error('Erreur envoi SMS:', error);
      return { success: false, method: 'sms', error: error.message };
    }
  }

  // Template Email
  getEmailTemplate(notification) {
    return `
      <!DOCTYPE html>
      <html>
      <head>
          <meta charset="utf-8">
          <style>
              body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
              .container { max-width: 600px; margin: 0 auto; padding: 20px; }
              .header { background: #007bff; color: white; padding: 20px; text-align: center; }
              .content { background: #f8f9fa; padding: 20px; margin: 20px 0; }
              .footer { text-align: center; padding: 20px; font-size: 12px; color: #666; }
              .urgent { border-left: 4px solid #dc3545; }
              .normal { border-left: 4px solid #007bff; }
              .button {
                  display: inline-block;
                  padding: 10px 20px;
                  background: #007bff;
                  color: white;
                  text-decoration: none;
                  border-radius: 5px;
                  margin: 10px 0;
              }
          </style>
      </head>
      <body>
          <div class="container">
              <div class="header">
                  <h1>${notification.title}</h1>
              </div>
              <div class="content ${notification.urgency === 'urgent' ? 'urgent' : 'normal'}">
                  <p>${notification.message}</p>
                  ${notification.dossierRef ? `<p><strong>Dossier:</strong> ${notification.dossierRef}</p>` : ''}
                  ${notification.urgency === 'urgent' ? '<p><em>⚠️ Cette notification est marquée comme urgente</em></p>' : ''}
                  <a href="${process.env.REACT_APP_FRONTEND_URL}/dashboard" class="button">
                      Voir dans l'application
                  </a>
              </div>
              <div class="footer">
                  <p>Vous recevez cet email car vous êtes abonné aux notifications de notre plateforme.</p>
                  <p><a href="${process.env.REACT_APP_FRONTEND_URL}/settings/notifications">Gérer mes préférences</a></p>
              </div>
          </div>
      </body>
      </html>
    `;
  }

  // Mettre à jour le statut de livraison
  async updateNotificationStatus(notificationId, results) {
    try {
      const deliveryStatus = {};
      results.forEach(result => {
        if (result.status === 'fulfilled' && result.value) {
          const method = result.value.method;
          deliveryStatus[method] = result.value.success ? 'delivered' : 'failed';
        }
      });

      await updateDoc(doc(db, 'notifications', notificationId), {
        deliveryStatus,
        lastUpdated: serverTimestamp()
      });
    } catch (error) {
      console.error('Erreur mise à jour statut:', error);
    }
  }

  // Méthodes spécifiques
  async notifyDossierUpdate(dossier, recipientId, updateType) {
    const notification = {
      recipientId,
      type: 'dossier_update',
      title: `Dossier ${dossier.reference || 'mis à jour'}`,
      message: `Le dossier a été mis à jour: ${updateType}`,
      urgency: 'normal',
      dossierRef: dossier.reference,
      dossierId: dossier.id
    };
    return this.sendNotification(notification);
  }

  async notifyDeadlineApproaching(dossier, recipientId, daysRemaining) {
    const urgency = daysRemaining <= 2 ? 'urgent' : 'normal';
    const notification = {
      recipientId,
      type: 'deadline_approaching',
      title: `Échéance proche ${dossier.reference ? '- ' + dossier.reference : ''}`,
      message: `Le dossier arrive à échéance dans ${daysRemaining} jour(s)`,
      urgency,
      dossierRef: dossier.reference,
      dossierId: dossier.id
    };
    return this.sendNotification(notification);
  }

  async notifyNewMessage(message, recipientId) {
    const notification = {
      recipientId,
      senderId: message.senderId,
      type: 'new_message',
      title: 'Nouveau message reçu',
      message: `Vous avez reçu un nouveau message${message.senderName ? ' de ' + message.senderName : ''}`,
      urgency: 'normal',
      dossierRef: message.dossierRef,
      dossierId: message.dossierId
    };
    return this.sendNotification(notification);
  }
}

export default new NotificationDispatcher();